import React, { useState, useRef, useEffect, useContext } from 'react';
import axios from 'axios';
import AppContext from '../../../context/AppContext.js';
import {
  Container, Button, Dropdown, Table,
  Modal, Form, Row, Col
} from 'react-bootstrap';
import {
  FaPlus, FaEllipsisV, FaArrowLeft, FaTimes,
  FaBold, FaItalic, FaAlignLeft, FaAlignCenter,
  FaAlignRight, FaImage, FaPaperclip, FaUndo, FaRedo
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import MasterLayout from '../../../layout/superadmin/MasterLayout';
import '../../../assets/css/superadmin/blogmanagement/BlogManagement.css';


// ══════════════════════════════════════════════
//  RICH EDITOR — Self-contained sub-component
// ══════════════════════════════════════════════
const RichEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('format');

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  const handleInput = () => {
    onChange(editorRef.current?.innerHTML || '');
  };

  const handleFileInsert = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    link.target = '_blank';
    link.textContent = file.name;
    editorRef.current?.focus();
    document.execCommand('insertHTML', false, link.outerHTML);
    onChange(editorRef.current?.innerHTML || '');
    event.target.value = '';
  };

  const handleImageInsert = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      exec('insertImage', reader.result);
      onChange(editorRef.current?.innerHTML || '');
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const toolbarButtons = {
    format: [
      { icon: <FaBold />, title: 'Bold', action: () => exec('bold') },
      { icon: <FaItalic />, title: 'Italic', action: () => exec('italic') },
      { type: 'divider' },
      { icon: <FaAlignLeft />, title: 'Align Left', action: () => exec('justifyLeft') },
      { icon: <FaAlignCenter />, title: 'Align Center', action: () => exec('justifyCenter') },
      { icon: <FaAlignRight />, title: 'Align Right', action: () => exec('justifyRight') },
      { type: 'divider' },
      { icon: <FaUndo />, title: 'Undo', action: () => exec('undo') },
      { icon: <FaRedo />, title: 'Redo', action: () => exec('redo') },
    ],
    insert: [
      { icon: <FaImage />, title: 'Insert Image', action: () => imageInputRef.current?.click() },
      { icon: <FaPaperclip />, title: 'Attach File', action: () => fileInputRef.current?.click() },
    ],
  };

  return (
    <div className="rte-wrapper">
      <div className="rte-tab-bar">
        {['format', 'insert'].map(tab => (
          <button
            key={tab}
            type="button"
            className={`rte-tab ${activeTab === tab ? 'rte-tab-active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="rte-toolbar">
        {toolbarButtons[activeTab].map((btn, i) =>
          btn.type === 'divider'
            ? <span key={i} className="rte-divider" />
            : (
              <button
                key={i}
                type="button"
                title={btn.title}
                className="rte-tool-btn"
                onMouseDown={(e) => { e.preventDefault(); btn.action(); }}
              >
                {btn.icon}
              </button>
            )
        )}
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="rte-editor"
        onInput={handleInput}
        data-placeholder="Write your blog description here..."
      />

      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileInsert} />
      <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageInsert} />
    </div>
  );
};

const BlogManagement = () => {
  const { baseUrl, baseUrlForFile,authToken } = useContext(AppContext);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [image, setImage] = useState(null);
  const [author, setAuthor] = useState();
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [keywords, setKeywords] = useState('');
  const [status, setStatus] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentBlogId, setCurrentBlogId] = useState(null);
  const [currentView, setCurrentView] = useState('list');
  const [editBlogData, setEditBlogData] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);


  // ── Slug auto-generate ───────────────────────
  const generateSlug = (val) =>
    val.toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-');



  const addBlog = () => {
    const formdata = new FormData();
    formdata.append('title', title);
    formdata.append('description', description);
    formdata.append('keywords', keywords);
    formdata.append('category', category);
    formdata.append('slug', slug);
    formdata.append('image', image);
    formdata.append('status', status);
    axios.post(`${baseUrl}/superadmin/blog/addBlog`, formdata, {
      headers:
      {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${authToken}`
      }
    }).then((response) => {
      console.log(response.data);
      viewAllBlogs();
      resetForm();
      setCurrentView('list');
      Swal.fire({ title: 'Blog Added!', icon: 'success', confirmButtonColor: '#FDB714', timer: 2000 });
    }).catch((error) => {
      console.log(error);
    })
  };

  const viewAllBlogs = () => {
    axios.get(`${baseUrl}/superadmin/blog/viewAllBlogs`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })
      .then((response) => {
        console.log("API Response:", response.data);
        if (response.data.success && response.data.data) {
          setBlogs(response.data.data);
        } else {
          setBlogs([]);
        }
      })
      .catch((error) => {
        console.log(error);
        setBlogs([]);
      });
  };

  const updateBlog = () => {
  const formdata = new FormData();
  formdata.append('id',          currentBlogId);
  formdata.append('title',       title);
  formdata.append('description', description);
  formdata.append('keywords',    keywords);
  formdata.append('category',    category);
  formdata.append('slug',        slug);
  formdata.append('status',      status);

  // ✅ Category management jaisa — sirf nayi image ho tab append karo
  if (image !== null && image !== undefined) {
    formdata.append('image', image);
  }

  axios.post(`${baseUrl}/superadmin/blog/updateBlog`, formdata, {
    headers: { 'Content-Type': 'multipart/form-data' ,
      Authorization: `Bearer ${authToken}`
    }
  }).then((response) => {
    console.log(response.data);
    viewAllBlogs();
    resetForm();
    setCurrentView('list');
    Swal.fire({ title: 'Blog Updated!', icon: 'success', confirmButtonColor: '#FDB714', timer: 2000 });
  }).catch((error) => {
    console.log(error);
  });
};
  const deleteBlog = (id) => {
    const formdata = new FormData();
    formdata.append('id', id);
    axios.post(`${baseUrl}/superadmin/blog/deleteBlog`, formdata, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${authToken}`
      }
    }).then((response) => {
      console.log(response.data);
      viewAllBlogs();
    }).catch((error) => {
      console.log(error);
    });
  };

  const ToggleStatus = (id, isCurrentlyActive) => {
    const formdata = new FormData();
    formdata.append('id', id);
    formdata.append('status', isCurrentlyActive ? 'inactive' : 'active');
    axios.post(`${baseUrl}/superadmin/blog/updateBlog`, formdata, {
      headers: { 'Content-Type': 'multipart/form-data' ,
        Authorization: `Bearer ${authToken}`
      },
    }).then(response => { console.log(response.data); viewAllBlogs(); })
      .catch(error => console.log(error));
  };

  // ── Reset form ───────────────────────────────
  const resetForm = () => {
    setTitle('');
    setSlug('');
    setImage(null);
    setDescription('');
    setCategory('');
    setKeywords('');
    setStatus('active');
    setImagePreview(null);
    setIsEditMode(false);
    setCurrentBlogId(null);
  };

  // ════════════════════════════════════════════
  //  LISTING HANDLERS
  // ════════════════════════════════════════════

  const AddBlogClick = () => { resetForm(); setEditBlogData(null); setCurrentView('form'); };

  const View = (blog) => { setSelectedBlog(blog); setShowViewModal(true); };

  const EditClick = (blog) => {
    Swal.fire({
      title: 'Opening Editor', text: 'Loading blog data...',
      icon: 'info', timer: 800, showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    }).then(() => { setEditBlogData(blog); setCurrentView('form'); });
  };

  const Delete = (blog) => {
    Swal.fire({
      title: 'Are you sure?', text: `Delete "${blog.title}"?`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#DC2626', cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it!', reverseButtons: true,
    }).then(result => {
      if (result.isConfirmed) {
        deleteBlog(blog.id);
        Swal.fire({ title: 'Deleted!', icon: 'success', confirmButtonColor: '#FDB714', timer: 2000 });
      }
    });
  };

  const RowClick = (blog, e) => {
    if (e.target.closest('.dropdown') || e.target.closest('.bm-table-actions')) return;
    EditClick(blog);
  };

  // ════════════════════════════════════════════
  //  FORM HANDLERS
  // ════════════════════════════════════════════

  // Populate individual states when entering edit mode
 useEffect(() => {
  viewAllBlogs();
  if (currentView === 'form') {
    if (editBlogData) {
      setIsEditMode(true);
      setCurrentBlogId(editBlogData.id);
      setTitle(editBlogData.title       || '');
      setSlug(editBlogData.slug         || '');
      setDescription(editBlogData.description || '');
      setKeywords(editBlogData.keywords || '');
      setCategory(editBlogData.category || '');
      setStatus(editBlogData.status     || 'active');
      setImage(null);   // ✅ null rakho — nayi image select hogi tabhi set hogi
      setImagePreview(
        editBlogData.image
          ? `${baseUrlForFile}/${editBlogData.image}`  // ✅ purani image preview mein dikhao
          : null
      );
    } else {
      resetForm();
    }
  }
}, [currentView, editBlogData]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ title: 'File Too Large', text: 'Max 5MB allowed', icon: 'error', confirmButtonColor: '#FDB714' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      Swal.fire({ title: 'Invalid File', text: 'Select a valid image', icon: 'error', confirmButtonColor: '#FDB714' });
      return;
    }
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    Swal.fire({
      title: 'Remove Image?', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#DC2626', confirmButtonText: 'Remove',
    }).then(result => {
      if (result.isConfirmed) { setImage(null); setImagePreview(null); }
    });
  };

  const stripHtml = (html) => html ? html.replace(/<[^>]*>/g, '').trim() : '';

  const validateAndSubmit = () => {
    const missing = [];
    if (!title) missing.push('Title');
    if (!stripHtml(description)) missing.push('Description');

    if (missing.length) {
      Swal.fire({
        title: 'Missing Required Fields',
        html: `<ul style="text-align:left">${missing.map(f => `<li>${f}</li>`).join('')}</ul>`,
        icon: 'error', confirmButtonColor: '#FDB714',
      });
      return false;
    }
    return true;
  };

  const handleSaveDraft = () => {
    if (!validateAndSubmit()) return;
    setTimeout(() => isEditMode ? updateBlog() : addBlog(), 0);
  };

  const handlePublish = () => {
    if (!validateAndSubmit()) return;
    Swal.fire({
      title: 'Publish Blog?', icon: 'question', showCancelButton: true,
      confirmButtonColor: '#FDB714', confirmButtonText: 'Yes, Publish!',
    }).then(result => {
      if (result.isConfirmed) {
        setTimeout(() => isEditMode ? updateBlog() : addBlog(), 0);
      }
    });
  };

  const handleBack = () => {
    Swal.fire({
      title: 'Unsaved Changes', text: 'Close without saving?',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#DC2626', confirmButtonText: 'Yes, close',
    }).then(result => { if (result.isConfirmed) setCurrentView('list'); });
  };

  // ════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════
  return (
    <MasterLayout>

      {/* ══════════════════════════
          LIST VIEW
      ══════════════════════════ */}
      {currentView === 'list' && (
        <div className="bm-blog-management-page">
          <Container fluid>

            <div className="bm-management-header">
              <div className="bm-header-content">
                <h1 className="bm-management-title">
                  Blog Hub <span className="bm-title-highlight">Management</span>
                </h1>
                <p className="bm-management-subtitle">
                  Create, edit, and manage informative content for AgriRent users.
                </p>
              </div>
              <Button className="bm-add-blog-btn" onClick={AddBlogClick}>
                <FaPlus className="bm-btn-icon" /> Add New Blog
              </Button>
            </div>

            {blogs.length > 0 ? (
              <div className="bm-table-container">
                <Table className="bm-blogs-table" hover>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Title</th>
                      {/* <th>Auther</th> */}
                      <th>Category</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.map((blog) => {
                      const isActive = blog.status === 'active';
                      return (
                        <tr key={blog.id} onClick={(e) => RowClick(blog, e)}>
                          <td data-label="Image">
                            <img src={`${baseUrlForFile}/${blog.image}`} alt={blog.title} className="bm-table-image" />
                          </td>
                          <td data-label="Title">
                            <span className="bm-table-title">{blog.title}</span>
                          </td>
                          {/* <td data-label="Author">
                            <span className="bm-table-author">{blog.author|| '—'}</span>
                          </td> */}
                          <td data-label="Category">
                            <span className="bm-table-category">{blog.category || '—'}</span>
                          </td>
                          <td data-label="Status">
                            <span className={`bm-table-status ${isActive ? 'bm-published' : 'bm-draft'}`}>
                              {isActive ? '● Active' : '● Inactive'}
                            </span>
                          </td>
                          <td className="bm-table-actions" data-label="Actions">
                            <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                              <Dropdown.Toggle variant="link" className="bm-menu-toggle" id={`dropdown-${blog.id}`}>
                                <FaEllipsisV />
                              </Dropdown.Toggle>
                              <Dropdown.Menu className="bm-custom-dropdown">
                                <Dropdown.Item onClick={(e) => { e.stopPropagation(); View(blog); }}>View</Dropdown.Item>
                                <Dropdown.Item onClick={(e) => { e.stopPropagation(); EditClick(blog); }}>Edit</Dropdown.Item>
                                <Dropdown.Item onClick={(e) => { e.stopPropagation(); ToggleStatus(blog.id, isActive); }}>
                                  {isActive ? 'Disable' : 'Enable'}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item className="bm-delete-option" onClick={(e) => { e.stopPropagation(); Delete(blog); }}>
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="bm-empty-state">
                <div className="bm-empty-icon">📝</div>
                <h3>No blogs found</h3>
                <p>Create your first blog post!</p>

              </div>
            )}
          </Container>

          {/* View Modal */}
          <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered className="bm-view-modal">
            <Modal.Header closeButton>
              <Modal.Title>Blog Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedBlog && (
                <>
                  <img src={`${baseUrlForFile}/${selectedBlog.image}`} alt={selectedBlog.title} className="bm-view-image" />
                  <h2 className="bm-view-title">{selectedBlog.title}</h2>
                  <div className="bm-view-meta">
                    {selectedBlog.category && <div className="bm-view-meta-item"><strong>Category:</strong> {selectedBlog.category}</div>}
                    {selectedBlog.keywords && <div className="bm-view-meta-item"><strong>Keywords:</strong> {selectedBlog.keywords}</div>}
                    <div className="bm-view-meta-item"><strong>Status:</strong> {selectedBlog.status}</div>
                    <div className="bm-view-meta-item"><strong>Slug:</strong> {selectedBlog.slug}</div>
                  </div>
                  <div className="bm-view-description" dangerouslySetInnerHTML={{ __html: selectedBlog.description }} />
                </>
              )}
            </Modal.Body>
          </Modal>
        </div>
      )}

      {/* ══════════════════════════
          FORM VIEW
      ══════════════════════════ */}
      {currentView === 'form' && (
        <div className="cbm-create-blog-page">
          <div className="cbm-page-header">
            <Container fluid>
              <div className="cbm-header-content-wrapper">
                <div className="cbm-header-left">
                  <button className="cbm-back-btn" onClick={handleBack}><FaArrowLeft /></button>
                  <div className="cbm-header-title-section">
                    <h1 className="cbm-page-title">{isEditMode ? 'Edit Post' : 'Create New Post'}</h1>
                    <p className="cbm-page-subtitle">AgriRent Blog Management</p>
                  </div>
                </div>
                <div className="cbm-header-actions">
                  <Button className="cbm-save-draft-btn" onClick={handleSaveDraft}>Save Draft</Button>
                  <Button className="cbm-publish-btn" onClick={handlePublish}>▶ Publish Post</Button>
                </div>
              </div>
            </Container>
          </div>

          <div className="cbm-page-content">
            <Container fluid>
              <Row className="justify-content-center">
                <Col lg={8}>
                  <div className="cbm-editor-container">

                    {/* Title */}
                    <Form.Group className="cbm-form-group">
                      <Form.Label className="cbm-field-label">Title *</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter blog title here"
                        className="cbm-input"
                        value={title}
                        onChange={(event) => {
                          setTitle(event.target.value);
                          setSlug(generateSlug(event.target.value));
                        }}
                      />
                    </Form.Group>

                    {/* Slug */}
                    <Form.Group className="cbm-form-group">
                      <Form.Label className="cbm-field-label">
                        Slug <span className="cbm-auto-label">(auto-generated)</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        className="cbm-input cbm-input-readonly"
                        value={slug}
                        readOnly
                      />
                    </Form.Group>

                    {/* Category */}
                    <Form.Group className="cbm-form-group">
                      <Form.Label className="cbm-field-label">Category</Form.Label>
                      <Form.Select
                        className="cbm-input"
                        value={category}
                        onChange={(event) => setCategory(event.target.value)}
                      >
                        <option value="">-- Select Category --</option>

                        <option >RentalTips</option>
                        <option >Maintenance</option>
                        <option >Agri-Tech</option>
                        <option >User Awareness</option>

                      </Form.Select>
                    </Form.Group>

                    {/* Keywords */}
                    <Form.Group className="cbm-form-group">
                      <Form.Label className="cbm-field-label">
                        Keywords <span className="cbm-auto-label">(comma separated)</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g. tractor, rental, farming"
                        className="cbm-input"
                        value={keywords}
                        onChange={(event) => setKeywords(event.target.value)}
                      />
                    </Form.Group>

                    {/* Featured Image */}
                    <Form.Group className="cbm-form-group">
                      <Form.Label className="cbm-field-label">Featured Image</Form.Label>
                      {imagePreview ? (
                        <div className="cbm-image-preview-container">
                          <img src={imagePreview} alt="Preview" className="cbm-image-preview" />
                          <button className="cbm-remove-image-btn" onClick={handleRemoveImage}>
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <div className="cbm-upload-area">
                          <label htmlFor="blog-image-upload" className="cbm-upload-label">
                            <div className="cbm-upload-icon">📷</div>
                            <span>Upload Featured Image</span>
                            <input
                              id="blog-image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                      )}
                    </Form.Group>

                    {/* Description — Rich Editor */}
                    <Form.Group className="cbm-form-group">
                      <Form.Label className="cbm-field-label">Description *</Form.Label>
                      <RichEditor
                        value={description}
                        onChange={(val) => setDescription(val)}
                      />
                    </Form.Group>

                    {/* Status */}
                    <Form.Group className="cbm-form-group">
                      <Form.Label className="cbm-field-label">Status</Form.Label>
                      <Form.Select
                        className="cbm-input"
                        value={status}
                        onChange={(event) => setStatus(event.target.value)}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Form.Select>
                    </Form.Group>

                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </div>
      )}

    </MasterLayout>
  );
};

export default BlogManagement;